import * as CL from '@design-system/component-library';
import classNames from 'classnames';
import { Link } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  adminMsg,
  administratorMsg,
  companyMsg,
  contactRequiresInvitationBeforeGrantingOrderApproverMsg,
  employeeOmaElisaMsg,
  invitationsPageMsg,
  omaElisaForCompaniesMsg,
  orderAcceptorMsg,
  selectAllMsg,
  t,
} from '../../common/i18n/index.js';
import { getCompanyName } from '../../common/utils/accountUtils.js';
import { isBeingInvited } from '../../common/utils/contactUtils.js';
import { InvitationStatus } from '../../generated/api/invitationStatus.js';
import { useAuth } from '../../public/site/AuthProvider.js';
import type { CLTableColumn } from '../../common/types/componentLibrary.js';
import type { ContactInvitation } from '../../generated/api/contactInvitation.js';
import type { Contact } from '../../generated/api/models.js';

enum AccessRightsTableField {
  COMPANY = 'company',
  ADMIN = 'admin',
  ORDER_ACCEPTOR = 'orderAcceptor',
}

const getInvitationDisplayValues = (invitation: ContactInvitation) => {
  switch (invitation.status) {
    case InvitationStatus.Created:
      return {
        icon: 'redo',
        color: 'orange-600',
        text: t.CB50('Sending...'),
      };
    case InvitationStatus.Sent:
      return {
        icon: 'clock',
        color: 'orange-600',
        text: t.DVAW('Waiting...'),
      };
    case InvitationStatus.Expired:
      return {
        icon: 'warning',
        color: 'red-600',
        text: t.PJQL(`Expired`),
      };
    case InvitationStatus.Error:
      return {
        icon: 'warning',
        color: 'red-600',
        text: t.NMHZ('Failed'),
      };
  }
  return {};
};

export const InvitationStatusCell = ({ invitation }: { invitation: ContactInvitation }) => {
  const { icon, color, text } = getInvitationDisplayValues(invitation);
  return (
    <div className="of-invitation-status">
      <CL.Icon icon={icon} color={color} /> {text}
    </div>
  );
};

const Checkmark = ({ ariaLabel }: { ariaLabel: string }) => (
  <CL.Icon icon="check" color="success" type="filled" aria-label={ariaLabel} />
);

const CompanyCell = ({ accountMasterId, isPrimaryCompany }: { accountMasterId: string; isPrimaryCompany: boolean }) => {
  const { authenticatedUser } = useAuth();
  return (
    <>
      <div className={isPrimaryCompany ? dsClass.TEXT_BOLD : ''}>
        {getCompanyName(authenticatedUser, accountMasterId)}
      </div>
      {isPrimaryCompany && <div className={dsClass.TEXT_XS}>{t.JO8H('Primary company')}</div>}
    </>
  );
};

interface AdminCellProps {
  accountMasterId: string;
  isChecked: boolean;
  isEdit: boolean;
  invitation?: ContactInvitation;
  onChange: () => void;
}

const AdminCell = ({ accountMasterId, isChecked, isEdit, invitation, onChange }: AdminCellProps) => {
  if (isEdit) {
    return (
      <CL.Checkbox
        id={accountMasterId + isChecked}
        key={accountMasterId + isChecked}
        checked={isChecked}
        onChange={onChange}
      />
    );
  }
  if (isBeingInvited(invitation) && isChecked) {
    return <InvitationStatusCell invitation={invitation!} />;
  }
  if (isChecked) {
    return <Checkmark ariaLabel={t.E112(administratorMsg)} />;
  }
  return null;
};

interface OrderAcceptorCellProps {
  accountMasterId: string;
  isChecked: boolean;
  isDisabled: boolean;
  isEdit: boolean;
  isPrimaryCompany: boolean;
  requiresInvitation: boolean;
  invitation?: ContactInvitation;
  onChange: () => void;
}

const OrderAcceptorCell = ({
  accountMasterId,
  isChecked,
  isDisabled,
  isEdit,
  isPrimaryCompany,
  requiresInvitation,
  invitation,
  onChange,
}: OrderAcceptorCellProps) => {
  if (!isPrimaryCompany) {
    return null;
  }
  if (isEdit) {
    return (
      <div className="of-contact-access-rights__order-acceptor-cell">
        <CL.Checkbox
          id={'acceptor' + accountMasterId + isChecked}
          key={'acceptor' + accountMasterId + isChecked}
          checked={isChecked}
          disabled={isDisabled}
          onChange={onChange}
        />
        {requiresInvitation && (
          <CL.Popover placement="right" triggerElement={<CL.Icon icon="warning" type="regular" />}>
            {t.DHEY(contactRequiresInvitationBeforeGrantingOrderApproverMsg)}{' '}
            <Link to={paths.COMPANY_INFO_INVITES}>{t.WM9Y(invitationsPageMsg)}</Link>
            {t.FHL4('.')}
          </CL.Popover>
        )}
      </div>
    );
  }
  if (isBeingInvited(invitation) && isChecked) {
    return <InvitationStatusCell invitation={invitation!} />;
  }
  return isChecked ? <Checkmark ariaLabel={t.POX2(orderAcceptorMsg)} /> : null;
};

interface AccessRightsTableProps {
  contact?: Contact;
  contactPrimaryMasterId: string;
  invitation?: ContactInvitation;
  isEdit: boolean;
  isApprover: boolean;
  isCommunityUser: boolean;
  setIsApprover: (isApprover: boolean) => void;
  selectedAccountMasterIds: string[];
  addAdminRight: (accountMasterId: string) => void;
  removeAdminRight: (accountMasterId: string) => void;
  toggleAllAdminRights: (e: React.ChangeEvent<HTMLInputElement>) => void;
}

export const AccessRightsTable = ({
  contactPrimaryMasterId,
  invitation,
  isEdit,
  isApprover,
  isCommunityUser,
  setIsApprover,
  selectedAccountMasterIds,
  addAdminRight,
  removeAdminRight,
  toggleAllAdminRights,
}: AccessRightsTableProps) => {
  const { authenticatedUser } = useAuth();
  if (!authenticatedUser) {
    return null;
  }
  const secondaryAccountMasterIds = authenticatedUser?.secondaryAccounts?.map(account => account.accountMasterId) ?? [];
  const otherAvailableAccountMasterIdsForAuthUser = [authenticatedUser.mdmId!, ...secondaryAccountMasterIds].filter(
    mdmId => mdmId !== contactPrimaryMasterId
  );
  const availableAccountMasterIds = [contactPrimaryMasterId, ...otherAvailableAccountMasterIdsForAuthUser];

  const isChecked = (accountMasterId: string) => selectedAccountMasterIds.includes(accountMasterId);

  const showSelectAllCheckbox = isEdit && availableAccountMasterIds.length > 1;
  const headerPaddingClass = showSelectAllCheckbox ? dsClass.PADDING_BOTTOM_6 : '';

  const columns: CLTableColumn[] = [
    {
      key: AccessRightsTableField.COMPANY,
      width: '40%',
      label: <div className={headerPaddingClass}>{t.KJTS(companyMsg)}</div>,
    },
    {
      key: AccessRightsTableField.ADMIN,
      width: '20%',
      align: 'center',
      label: (
        <>
          <div>{t.E112(adminMsg)}</div>
          <div className={classNames(dsClass.FONT_WEIGHT_400, dsClass.TEXT_XS)}>{t.LOUS(omaElisaForCompaniesMsg)}</div>
          {showSelectAllCheckbox && (
            <CL.Checkbox onChange={toggleAllAdminRights} label={t.A8VA(selectAllMsg)}></CL.Checkbox>
          )}
        </>
      ),
    },
    {
      key: AccessRightsTableField.ORDER_ACCEPTOR,
      width: '20%',
      align: 'center',
      label: (
        <div className={classNames(headerPaddingClass, 'of-contact-access-rights__order-acceptor-header')}>
          <div>
            <div>{t.POX2(orderAcceptorMsg)}</div>
            <div className={classNames(dsClass.FONT_WEIGHT_400, dsClass.TEXT_XS)}>{t.H6JV(employeeOmaElisaMsg)}</div>
          </div>
          <CL.Tooltip
            placement="right"
            triggerElement={<CL.Icon icon="information" type="regular" size="s" aria-hidden="true" />}
            i18n_tooltip_contentText={t.WQM0(
              'You can assign the order approver role to a user only within their employer company.'
            )}
          />
        </div>
      ),
    },
    { key: 'empty', width: '20%' },
  ];

  const rows =
    availableAccountMasterIds?.map(accountMasterId => {
      const adminChecked = isChecked(accountMasterId);
      return {
        [AccessRightsTableField.COMPANY]: (
          <CompanyCell
            accountMasterId={accountMasterId}
            isPrimaryCompany={accountMasterId === contactPrimaryMasterId}
          />
        ),
        [AccessRightsTableField.ADMIN]: (
          <AdminCell
            accountMasterId={accountMasterId}
            isChecked={adminChecked}
            isEdit={isEdit}
            invitation={invitation}
            onChange={() => (adminChecked ? removeAdminRight(accountMasterId) : addAdminRight(accountMasterId))}
          />
        ),
        [AccessRightsTableField.ORDER_ACCEPTOR]: (
          <OrderAcceptorCell
            accountMasterId={accountMasterId}
            isChecked={isApprover}
            isDisabled={adminChecked}
            isEdit={isEdit}
            isPrimaryCompany={accountMasterId === contactPrimaryMasterId}
            requiresInvitation={!isCommunityUser}
            invitation={invitation}
            onChange={() => setIsApprover(!isApprover)}
          />
        ),
      };
    }) ?? [];

  return <CL.Table columns={columns} rows={rows} tableType="striped" />;
};
